import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";

const emptyForm = { name: "", type: "product", price: 0, monthly_price: null, requires_document: false, requires_selfie: false, requires_terms: false, commission_type: null, commission_value: null };

export default function ProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (isEdit) api.get(`/products/${id}`).then((r) => setForm(r.data)).catch(() => navigate("/crm/products")); }, [id]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), monthly_price: form.monthly_price ? Number(form.monthly_price) : null, commission_value: form.commission_value ? Number(form.commission_value) : null };
      if (isEdit) await api.put(`/products/${id}`, payload);
      else await api.post("/products", payload);
      navigate("/crm/products");
    } catch { setError("Erro ao salvar produto."); }
    setSaving(false);
  };

  return (
    <div data-testid="product-form-page">
      <button onClick={() => navigate("/crm/products")} className="inline-flex items-center gap-2 text-sm mb-6 transition-colors" style={{ color: "var(--crm-text-muted)" }}>
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="crm-heading mb-8">{isEdit ? "Editar Produto" : "Novo Produto"}</h1>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        {error && <div className="p-4 rounded-xl text-sm" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444" }}>{error}</div>}

        <div className="crm-card p-6 space-y-5">
          <h2 className="crm-subheading">Informações do Produto</h2>
          <div>
            <label className="crm-label">Nome *</label>
            <input required value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="product-name-input" className="crm-input" placeholder="Nome do produto" />
          </div>
          <div>
            <label className="crm-label">Tipo</label>
            <select value={form.type} onChange={(e) => handleChange("type", e.target.value)} data-testid="product-type-select" className="crm-input">
              <option value="product">Produto</option><option value="service">Serviço</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="crm-label">Preço (único) ¥</label>
              <input type="number" min="0" value={form.price} onChange={(e) => handleChange("price", e.target.value)} data-testid="product-price-input" className="crm-input" />
            </div>
            <div>
              <label className="crm-label">Preço Mensal ¥ (opcional)</label>
              <input type="number" min="0" value={form.monthly_price || ""} onChange={(e) => handleChange("monthly_price", e.target.value || null)} data-testid="product-monthly-price-input" className="crm-input" placeholder="Deixe vazio se não aplicável" />
            </div>
          </div>
        </div>

        <div className="crm-card p-6 space-y-4">
          <h2 className="crm-subheading">Requisitos</h2>
          {[
            { key: "requires_document", label: "Requer documento", testId: "product-requires-doc" },
            { key: "requires_selfie", label: "Requer selfie", testId: "product-requires-selfie" },
            { key: "requires_terms", label: "Requer aceite de termos", testId: "product-requires-terms" },
          ].map((r) => (
            <label key={r.key} className="flex items-center gap-3 cursor-pointer py-1">
              <input type="checkbox" checked={form[r.key]} onChange={(e) => handleChange(r.key, e.target.checked)} data-testid={r.testId}
                className="w-5 h-5 rounded border-2 accent-orange-500" style={{ borderColor: "var(--crm-border-strong)" }} />
              <span className="text-sm" style={{ color: "var(--crm-text-secondary)" }}>{r.label}</span>
            </label>
          ))}
        </div>

        {/* Commission */}
        <div className="crm-card p-6 space-y-4">
          <h2 className="crm-subheading">Comissão do Operador</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="crm-label">Tipo de Comissão</label>
              <select value={form.commission_type || ""} onChange={(e) => handleChange("commission_type", e.target.value || null)} data-testid="product-commission-type" className="crm-input">
                <option value="">Sem comissão</option>
                <option value="fixed">Valor Fixo (¥)</option>
                <option value="percentage">Percentual (%)</option>
              </select>
            </div>
            <div>
              <label className="crm-label">{form.commission_type === "percentage" ? "Percentual (%)" : "Valor (¥)"}</label>
              <input type="number" min="0" step="0.01" value={form.commission_value || ""} onChange={(e) => handleChange("commission_value", e.target.value || null)}
                data-testid="product-commission-value" className="crm-input" placeholder={form.commission_type === "percentage" ? "Ex: 10" : "Ex: 500"} disabled={!form.commission_type} />
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="product-save-btn" className="crm-btn-primary">{saving ? "Salvando..." : isEdit ? "Atualizar" : "Criar Produto"}</button>
          <button type="button" onClick={() => navigate("/crm/products")} className="crm-btn-secondary">Cancelar</button>
        </div>
      </form>
    </div>
  );
}
